import { useEffect, useState } from 'react'
import { Mixpanel } from 'Utils/Mixpanel'
import CardPricing from './Components/CardPricing'
import CheckoutStatus from './Components/CheckoutStatus'
import FAQsPricing from './Components/FAQsPricing'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DASHBAORD_ROUTER, SIGNUP_ROUTER } from 'constant/router'
import useSessionStore from 'Context/useSessionStore'
import { useCompany, usePayment } from 'Hooks'
import { Loading } from 'Organisms/Loading'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import { GET_LIST_PLAN_PACKAGES, OPTIONS_PRINCING } from 'Utils/const/packages'
import { getPendingCheckout, setPendingCheckout } from 'Utils/pendingCheckout'
import { Helmet } from 'react-helmet'
import { trackEvent } from 'Utils/GoogleAnalytics'

const PricingPage = () => {
  const { user, updateUser } = useSessionStore((state) => ({ user: state.user, updateUser: state.updateUser }))

  const [searchParams] = useSearchParams()

  // `checkout` is set by Stripe's return URLs: `success` (with session_id) or
  // `canceled`. Older links used `true` — treat that as success.
  const checkoutParam = searchParams.get('checkout')
  const checkoutOutcome = checkoutParam === 'canceled' ? 'canceled' : (checkoutParam ? 'success' : null)
  const checkoutSessionId = searchParams.get('session_id') || getPendingCheckout()?.sessionId || null

  const navigate = useNavigate()

  const { selectPlanCompanyHook, companyInfoHook, getCompanyInfoHook } = useCompany()
  const { getPaymentConnectHook } = usePayment()

  const [selectedPeriod, setSelectedPeriod] = useState(OPTIONS_PRINCING.annually.name)
  const [loading, setLoading] = useState(false)
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(!!user)
  const [processingPlan, setProcessingPlan] = useState(null)
  const [message, setMessage] = useState({ open: false })

  const PLANS = GET_LIST_PLAN_PACKAGES({ period: selectedPeriod, hiddenCustom: !!user })
  const currentPlanId = user ? companyInfoHook?.company_plan_package?.plan_package_id : null

  // Logged-in users are already on a plan. Fetch it so the card for that plan
  // can show "Current Plan" instead of inviting the user to pick it again.
  useEffect(() => {
    if (!user) return

    ;(async () => {
      try {
        await getCompanyInfoHook({ companyId: user.company_id })
      } finally {
        setLoadingCompanyInfo(false)
      }
    })()
  }, [])

  const choosePlan = async ({ plan }) => {
    const clickedPlan = Object.values(PLANS).find((p) => p.plan === plan)
    const professionalPrice = selectedPeriod === OPTIONS_PRINCING.annually.name ? OPTIONS_PRINCING.annually.price : OPTIONS_PRINCING.monthly.price
    const planPrice = plan === PLANS.PROFESSIONAL.plan
      ? professionalPrice
      : plan === PLANS.FREE.plan
        ? 0
        : null

    if (!user) {
      await trackEvent({
        category: 'Pricing Page',
        action: 'Choose Plan',
        label: plan
      })

      Mixpanel.track('Pricing Page Interaction', {
        event_type: 'Plan Clicked',
        plan_name: clickedPlan?.title || plan,
        plan_price: planPrice,
        billing_cycle: selectedPeriod,
        timestamp: new Date().toISOString()
      })

      return navigate(SIGNUP_ROUTER)
    }

    if (plan === PLANS.FREE.plan) {
      Mixpanel.track('Pricing Page Interaction', {
        event_type: 'Plan Clicked',
        plan_name: 'Starter',
        plan_price: 0,
        billing_cycle: selectedPeriod,
        timestamp: new Date().toISOString()
      })
      return selectPlanCompany({ plan_id: PLANS.FREE.id, period: selectedPeriod })
    }

    if (plan === PLANS.PROFESSIONAL.plan) {
      Mixpanel.track('Pricing Page Interaction', {
        event_type: 'Plan Clicked',
        plan_name: 'Professional',
        plan_price: selectedPeriod === OPTIONS_PRINCING.annually.name ? OPTIONS_PRINCING.annually.price : OPTIONS_PRINCING.monthly.price,
        billing_cycle: selectedPeriod,
        timestamp: new Date().toISOString()
      })
      try {
        setProcessingPlan(plan)
        const res = await getPaymentConnectHook({ period: selectedPeriod })

        if (!res.success || !res.data?.url) {
          setProcessingPlan(null)
          return setMessage({
            open: true,
            error: true,
            message: 'An error has occurred'
          })
        }

        // The hosted checkout URL embeds the session id (cs_test_… / cs_live_…).
        // Persist it so the app can show an "activating" state and re-check
        // status even if the user navigates away before the redirect completes.
        const sessionId = String(res.data.url).match(/(cs_(?:test|live)_[A-Za-z0-9]+)/)?.[1]
        setPendingCheckout({ sessionId, period: selectedPeriod })

        window.location.href = res.data.url
      } catch {
        setProcessingPlan(null)
        setMessage({ open: true, error: true, message: 'An error has occurred' })
      }
    }
  }

  const selectPlanCompany = async ({ plan_id, period }) => {
    try {
      setLoading(true)
      const res = await selectPlanCompanyHook({
        companyId: user.company_id,
        period,
        plan_package_id: plan_id
      })

      if (!res.success) {
        setLoading(false)
        return console.error('error')
      }

      await updateUser({
        ...user,
        company_is_active: true
      })
      navigate(DASHBAORD_ROUTER)
    } finally {
      setLoading(false)
    }
  }

  if (checkoutOutcome) {
    return <CheckoutStatus outcome={checkoutOutcome} sessionId={checkoutSessionId} />
  }

  return (
    <>
      <Helmet>
        <title>Plans & Pricing</title>
        <meta
          name="description"
          content="Time and task Tracking. Free trial available. Three pricing plans available."
        />
        <meta
          name="keywords"
          content="time tracker, time tracking, remote employees, productivity, software, remote work"
        />
        <meta name="author" content="Pulse by Canned Head" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Plans & Pricing" />
        <meta
          property="og:description"
          content="Time and task Tracking. Free trial available. Three pricing plans available."
        />
        <meta property="og:image" content="https://example.com/image.jpg" />
        <meta property="og:url" content="https://example.com/time-tracking-software" />
        <meta property="og:type" content="website" />
      </Helmet>

      {(loading || loadingCompanyInfo) && <Loading />}
      <div className={`landing_img_text_bg ${user ? 'pb-5' : ''}`}>
        <div className="containers pricing pt-4">
          <div className="row justify-content-center py-5">
            { !user
              ? <h1 className="col-12 text-center">Pricing</h1>
              : <h1 className="col-12 text-center">Choose a plan</h1>
            }
          </div>
          <div className="row justify-content-center mb-4">
            <div className="col-auto">
              <div className="btn-group">
                <button
                  type="button"
                  onClick={() => setSelectedPeriod(OPTIONS_PRINCING.monthly.name)}
                  className={`btn btn-secondary ${selectedPeriod === OPTIONS_PRINCING.monthly.name ? 'active2' : ''}`}
                  aria-current="page">
                  Monthly billing
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPeriod(OPTIONS_PRINCING.annually.name)}
                  className={`btn btn-secondary ${selectedPeriod === OPTIONS_PRINCING.annually.name ? 'active2' : ''}`}>
                  Yearly billing <span className="badge text-bg-primary">up to -20%</span>
                </button>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            {Object.values(PLANS).map((plan) => {
              const isCurrentPlan = !!currentPlanId && plan?.id === currentPlanId

              return (
                <CardPricing
                  key={plan.plan}
                  title={plan?.title || ''}
                  mode={plan?.mode || ''}
                  creditCardRequired={plan?.creditCardRequired}
                  description={plan?.description || ''}
                  btnChoose={isCurrentPlan ? 'Current Plan' : plan?.btnChoose || ''}
                  including={plan?.including || []}
                  onChoosePlan={() => choosePlan({ plan: plan?.plan || '' })}
                  period={plan?.period || ''}
                  btnColor={isCurrentPlan ? 'btn-secondary' : plan?.btnColor || ''}
                  loading={!!processingPlan && processingPlan === plan?.plan}
                  disabled={!!processingPlan || isCurrentPlan}
                />
              )
            })}
          </div>
          { !user && <FAQsPricing /> }
        </div>
      </div>

      {message.open && (
        <ModalSuccess
          visible={true}
          setVisible={() => setMessage({ open: false, message: '', error: false })}
          title={message.message}
          identifier="modalPayment"
          error={message.error}
        />
      )}
    </>
  )
}

export default PricingPage
